// src/album-art-modified.js
// (Your provided code for albumArt module)
module.exports.albumArt = async (artist, options, cb) => {
    // Massage inputs
    if (typeof artist !== "string") {
        throw new TypeError("Expected search query to be a string");
    }

    if (typeof options === "function") {
        cb = options;
        options = null;
    }

    if (typeof cb !== "function") {
        cb = null;
    }

    // Default options
    artist = artist.replace("&", "and");
    const opts = Object.assign({
        album: null,
        size: null,
    }, options);

    // Image size options
    const SIZES = {
        SMALL: "small",
        MEDIUM: "medium",
        LARGE: "large",
    };

    // Public Key on purpose - don't make me regret this
    // WARNING: These are placeholder/mock endpoints and will need to be changed
    // to point to a real API for the script to fetch actual data.
    const apiEndpoint = "https://api.spotify.com/v1";
    const authEndpoint = "https://accounts.spotify.com/api/token";
    const clientId = "3f974573800a4ff5b325de9795b8e603";
    const clientSecret = "ff188d2860ff44baa57acc79c121a3b9";

    // Create request URL
    const method = (opts.album === null) ? "artist" : "album";
    // Note: The limit=2 here means the underlying API call fetches a maximum of 2 items.
    // To get a list of 5 distinct albums directly from this function, this part and the
    // subsequent processing logic would need significant modification.
    const queryParams = `?q=artist:${encodeURIComponent(artist)}${method === "album" ? "%20album:" + encodeURIComponent(opts.album) : ""}&type=${method}&limit=2`;
    const searchUrl = `${apiEndpoint}/search${queryParams}`;
    const authString = `${clientId}:${clientSecret}`;

    let authorization;
    if (typeof btoa !== "undefined") {
        authorization = btoa(authString);
    } else if (Buffer) {
        authorization = Buffer.from(authString).toString("base64");
    } else {
        // Consider providing a polyfill or a different approach for Node.js environments
        // if Buffer is not available and btoa is not in the global scope.
        if (cb) return cb(new Error("No suitable environment found (btoa or Buffer missing)"), null);
        return Promise.reject(new Error("No suitable environment found (btoa or Buffer missing)"));
    }

    // Start by authorizing a session
    let authToken;
    try {
        const authResponse = await fetch(authEndpoint, {
            method: "POST", // Typically POST for OAuth token requests
            body: "grant_type=client_credentials",
            headers: {
                "Content-Type": "application/x-www-form-urlencoded",
                Authorization: `Basic ${authorization}`,
            },
        });
        if (!authResponse.ok) {
            const errorText = await authResponse.text();
            throw new Error(`Auth API Error ${authResponse.status}: ${errorText}`);
        }
        const authJson = await authResponse.json();
        authToken = authJson.access_token;
        if (!authToken) {
            throw new Error("Access token not received from auth endpoint.");
        }
    } catch (e) {
        if (cb) return cb(e, null);
        return Promise.reject(e);
    }


    // Perform image search
    let error = null;
    const response = await fetch(searchUrl, {
        method: "get",
        headers: {
            // "Content-Type": "application/x-www-form-urlencoded", // GET requests typically don't have a Content-Type for the body
            Authorization: `Bearer ${authToken}`,
        },
    }).then((res) => {
        if (!res.ok) {
            // More detailed error from API
            return res.text().then(text => {
                let detail = text;
                try { detail = JSON.parse(text).error.message; } catch (ign) {}
                throw new Error(`API Error ${res.status}: ${detail}`);
            });
        }
        return res.json();
    }).then((json) => {
        if (typeof (json.error) !== "undefined") {
            return Promise.reject(new Error(`JSON - ${json.error.message || json.error}`));
        }

        const itemsKey = method + "s"; // e.g., "artists" or "albums"
        if (!json[itemsKey] || !json[itemsKey].items || json[itemsKey].items.length === 0) {
            return Promise.reject(new Error("No results found"));
        }

        // This part processes only the FIRST item from the API result.
        const item = json[itemsKey].items[0];
        if (!item.images || item.images.length === 0) {
            // If the primary item has no images, try to find an artist image if an album was searched.
            // This is a specific interpretation; adjust if primary item should always have images.
            if (method === "album" && item.artists && item.artists[0] && json.artists && json.artists.items.length > 0 && json.artists.items[0].images && json.artists.items[0].images.length > 0) {
                //This is a bit of a stretch, ideally the album itself should have images.
                //Or the API structure needs to be known to fetch artist image if album image is missing.
                //For now, let's assume if album has no images, we can't show anything for it.
                return Promise.reject(new Error(`No images found for ${item.name}`));
            } else if (!item.images || item.images.length === 0) {
                return Promise.reject(new Error(`No images found for ${item.name}`));
            }
        }
        const images = item.images;


        let smallest = images[0];
        let largest = images[0];
        let medium = images.length > 1 ? images[1] : images[0]; // Default medium

        for (const element of images) {
            if (parseInt(element.width) < parseInt(smallest.width)) {
                smallest = element;
            }
            if (parseInt(element.width) > parseInt(largest.width)) {
                largest = element;
            }
            // A more robust way to find medium might be to sort and pick middle,
            // but for 3 Spotify images (large, medium, small), this is often index 1.
            if (images.length > 2 && parseInt(element.width) > parseInt(smallest.width) && parseInt(element.width) < parseInt(largest.width)) {
                medium = element;
            }
        }

        if (opts.size === SIZES.SMALL) {
            return smallest.url;
        }

        if (opts.size === SIZES.MEDIUM) {
            return medium.url;
        }

        // Large by default
        return largest.url;
    }).catch((error_) => {
        error = error_; // This error is shadowed, careful with scope.
        return Promise.reject(error_); // Ensure the promise chain rejects with the error.
    });

    // Callback
    if (cb) {
        // The catch block above already returns a rejected promise,
        // so this callback logic might need adjustment based on whether 'response' here
        // is the success value or the error object caught.
        // Assuming 'response' is the result if successful, 'error' is populated if failed.
        if (error) return cb(error, null);
        return cb(null, response); // 'response' here is the URL string or the rejected error
    }

    // Promise
    return response; // This will be the image URL or a rejected promise
};